import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Funnel, User, Briefcase, CaretDown, X, Trash } from "@phosphor-icons/react";

const STAGE_META = {
  new_vacancy: { label: "New Vacancy", color: "#6B7280" },
  vacancy_assigned: { label: "Vacancy Assigned", color: "#3B82F6" },
  candidate_search: { label: "Candidate Search", color: "#8B5CF6" },
  candidate_shortlisted: { label: "Shortlisted", color: "#A855F7" },
  cv_submitted: { label: "CV Submitted", color: "#EC4899" },
  client_reviewing: { label: "Client Reviewing", color: "#F59E0B" },
  interview_requested: { label: "Interview Req.", color: "#F97316" },
  interview_scheduled: { label: "Interview Sched.", color: "#EF4444" },
  interview_confirmed: { label: "Interview Conf.", color: "#14B8A6" },
  first_interview: { label: "1st Interview", color: "#06B6D4" },
  second_interview: { label: "2nd Interview", color: "#0EA5E9" },
  offer_made: { label: "Offer Made", color: "#10B981" },
  offer_accepted: { label: "Offer Accepted", color: "#22C55E" },
  placement_confirmed: { label: "Placement Conf.", color: "#16A34A" },
  invoice_generated: { label: "Invoice Gen.", color: "#C5A059" },
  invoice_sent: { label: "Invoice Sent", color: "#B8933F" },
  invoice_paid: { label: "Invoice Paid", color: "#1E3A8A" },
  placement_complete: { label: "Complete", color: "#0F172A" },
};
const STAGE_KEYS = Object.keys(STAGE_META);

export default function PlacementPipelinePage() {
  const { user } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ team_id: "", recruiter_id: "", company_id: "", vacancy_id: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [vacancies, setVacancies] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.team_id) params.set("team_id", filters.team_id);
      if (filters.recruiter_id) params.set("recruiter_id", filters.recruiter_id);
      if (filters.company_id) params.set("company_id", filters.company_id);
      if (filters.vacancy_id) params.set("vacancy_id", filters.vacancy_id);
      params.set("limit", "500");
      const { data } = await api.get(`/placements?${params}`);
      setPlacements(data.items || []);
    } catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [t, u, v] = await Promise.all([api.get("/teams"), api.get("/users"), api.get("/vacancies?limit=200")]);
        setTeams(t.data || []);
        setUsers(u.data || []);
        setVacancies(v.data?.items || []);
      } catch {}
    };
    if (user?.role === "admin") loadFilters();
  }, [user]);

  const handleDelete = async (placement, e) => {
    e.stopPropagation();
    const who = placement.candidate_name || "this entry";
    if (!window.confirm(`Remove ${who} from the pipeline? This cannot be undone.`)) return;
    const previous = placements;
    setPlacements(prev => prev.filter(p => p.id !== placement.id));   // optimistic
    try {
      await api.delete(`/placements/${placement.id}`);
      toast.success("Removed from pipeline");
    } catch (err) {
      setPlacements(previous);                                        // roll back
      toast.error(err.response?.data?.detail || "Failed to remove from pipeline");
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const newStage = result.destination.droppableId;
    const placementId = result.draggableId;
    if (newStage === result.source.droppableId) return;
    setPlacements(prev => prev.map(p => p.id === placementId ? { ...p, stage: newStage } : p));
    try {
      await api.put(`/placements/${placementId}/stage`, { stage: newStage });
      toast.success(`Moved to ${STAGE_META[newStage]?.label}`);
    } catch {
      toast.error("Failed to update stage");
      loadData();
    }
  };

  const grouped = {};
  STAGE_KEYS.forEach(k => { grouped[k] = []; });
  placements.forEach(p => {
    if (grouped[p.stage]) grouped[p.stage].push(p);
    else if (grouped.new_vacancy) grouped.new_vacancy.push(p);
  });

  if (loading) return <div className="flex gap-3 overflow-x-auto p-4">{[1,2,3,4].map(i => <div key={i} className="w-64 h-96 skeleton-shimmer rounded-lg flex-shrink-0" />)}</div>;

  return (
    <div data-testid="placement-pipeline-page" className="h-full flex flex-col -m-4 md:-m-6">
      <div className="px-4 md:px-6 py-4 border-b border-[#E5E7EB] bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#0F172A]">Placement Pipeline</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">{placements.length} active placement{placements.length !== 1 ? "s" : ""}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1" data-testid="toggle-filters-btn">
            <Funnel size={14} /> Filters {showFilters ? <X size={12}/> : <CaretDown size={12}/>}
          </Button>
        </div>
        {showFilters && user?.role === "admin" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="pipeline-filters">
            <select className="border rounded-lg px-2 py-1.5 text-xs" value={filters.team_id} onChange={e => setFilters({...filters, team_id: e.target.value})}>
              <option value="">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="border rounded-lg px-2 py-1.5 text-xs" value={filters.recruiter_id} onChange={e => setFilters({...filters, recruiter_id: e.target.value})}>
              <option value="">All Recruiters</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select className="border rounded-lg px-2 py-1.5 text-xs" value={filters.vacancy_id} onChange={e => setFilters({...filters, vacancy_id: e.target.value})}>
              <option value="">All Vacancies</option>
              {vacancies.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
            <Button variant="ghost" size="sm" onClick={() => setFilters({ team_id: "", recruiter_id: "", company_id: "", vacancy_id: "" })} className="text-xs">Clear</Button>
          </div>
        )}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-3 p-4 min-w-max h-full">
            {STAGE_KEYS.map(stageKey => {
              const meta = STAGE_META[stageKey];
              const items = grouped[stageKey] || [];
              return (
                <Droppable key={stageKey} droppableId={stageKey}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      className={`w-56 flex-shrink-0 flex flex-col rounded-lg border transition-colors ${snapshot.isDraggingOver ? "bg-blue-50 border-blue-300" : "bg-[#F9FAFB] border-[#E5E7EB]"}`}>
                      <div className="px-3 py-2.5 border-b border-[#E5E7EB] flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                          <span className="text-[11px] font-semibold text-[#0F172A] truncate">{meta.label}</span>
                          <span className="ml-auto text-[10px] font-medium text-[#6B7280] bg-white px-1.5 py-0.5 rounded">{items.length}</span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                        {items.map((p, idx) => (
                          <Draggable key={p.id} draggableId={p.id} index={idx}>
                            {(dragProvided, dragSnapshot) => (
                              <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}
                                className={`bg-white rounded-lg border p-2.5 text-left cursor-grab active:cursor-grabbing transition-shadow ${dragSnapshot.isDragging ? "shadow-lg border-blue-300" : "border-[#E5E7EB] hover:shadow-sm"}`}
                                data-testid={`placement-card-${p.id}`}>
                                <div className="flex items-start gap-1">
                                  <p className="text-xs font-semibold text-[#0F172A] truncate flex items-center gap-1 flex-1 min-w-0">
                                    <User size={12} className="text-[#C5A059] flex-shrink-0" />
                                    {p.candidate_name || "Unknown"}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => handleDelete(p, e)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Remove from pipeline"
                                    aria-label={`Remove ${p.candidate_name || "entry"} from pipeline`}
                                    className="flex-shrink-0 p-0.5 rounded text-[#9CA3AF] hover:text-[#EF4444] hover:bg-red-50 transition-colors"
                                    data-testid={`delete-placement-${p.id}`}>
                                    <Trash size={12} />
                                  </button>
                                </div>
                                <p className="text-[10px] text-[#6B7280] truncate mt-0.5 flex items-center gap-1">
                                  <Briefcase size={10} className="flex-shrink-0" />
                                  {p.vacancy_title || "—"}
                                </p>
                                {p.company_name && <p className="text-[10px] text-[#1E3A8A] truncate">{p.company_name}</p>}
                                {p.salary && <p className="text-[10px] text-[#C5A059] font-medium mt-1">{"\u00a3"}{p.salary}</p>}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}
